import type {
  HotelStepsData,
  GroupedQuestionnaireData,
  StandardQuestionnaireData,
  Answer,
} from '../../types/api';
import type {
  HotelQuestionnaireAction,
  StepsLoadingState,
} from '../types/hotelQuestionnaireTypes';

// Action creators
export const hotelQuestionnaireActions = {
  setHotelId: (hotelId: string): HotelQuestionnaireAction => ({
    type: 'SET_HOTEL_ID',
    payload: hotelId,
  }),

  setStepsLoading: (loading: StepsLoadingState): HotelQuestionnaireAction => ({
    type: 'SET_STEPS_LOADING',
    payload: loading,
  }),

  setStepsData: (stepsData: HotelStepsData): HotelQuestionnaireAction => ({
    type: 'SET_STEPS_DATA',
    payload: stepsData,
  }),

  setStepsError: (error: string | null): HotelQuestionnaireAction => ({
    type: 'SET_STEPS_ERROR',
    payload: error,
  }),

  setQuestionnaireLoading: (loading: boolean): HotelQuestionnaireAction => ({
    type: 'SET_QUESTIONNAIRE_LOADING',
    payload: loading,
  }),

  setQuestionnaireData: (
    data: GroupedQuestionnaireData | StandardQuestionnaireData
  ): HotelQuestionnaireAction => ({
    type: 'SET_QUESTIONNAIRE_DATA',
    payload: data,
  }),

  setQuestionnaireError: (error: string | null): HotelQuestionnaireAction => ({
    type: 'SET_QUESTIONNAIRE_ERROR',
    payload: error,
  }),

  setAnswers: (answers: Record<string, Answer>): HotelQuestionnaireAction => ({
    type: 'SET_ANSWERS',
    payload: answers,
  }),

  setDraftAnswers: (
    draftAnswers: Record<string, Answer>
  ): HotelQuestionnaireAction => ({
    type: 'SET_DRAFT_ANSWERS',
    payload: draftAnswers,
  }),

  clearDraftAnswers: (): HotelQuestionnaireAction => ({
    type: 'CLEAR_DRAFT_ANSWERS',
  }),

  applyDraftAnswers: (): HotelQuestionnaireAction => ({
    type: 'APPLY_DRAFT_ANSWERS',
  }),

  updateAnswer: (
    questionId: string,
    value: any,
    note?: string
  ): HotelQuestionnaireAction => ({
    type: 'UPDATE_ANSWER',
    payload: { questionId, value, note },
  }),

  updateDraftAnswer: (
    questionId: string,
    value: any,
    note?: string
  ): HotelQuestionnaireAction => ({
    type: 'UPDATE_DRAFT_ANSWER',
    payload: { questionId, value, note },
  }),
  updateSources: (
    stepKey: string,
    questions: any[]
  ): HotelQuestionnaireAction => ({
    type: 'UPDATE_SOURCES',
    payload: { stepKey, questions },
  }),

  updateCurrentStep: (stepKey: string): HotelQuestionnaireAction => ({
    type: 'UPDATE_CURRENT_STEP',
    payload: stepKey,
  }),

  setSourcesLoading: (loading: boolean): HotelQuestionnaireAction => ({
    type: 'SET_SOURCES_LOADING',
    payload: loading,
  }),

  setSourcesData: (data: any): HotelQuestionnaireAction => ({
    type: 'SET_SOURCES_DATA',
    payload: data,
  }),

  setTargetSourceGroup: (
    groupKey: string | null
  ): HotelQuestionnaireAction => ({
    type: 'SET_TARGET_SOURCE_GROUP',
    payload: groupKey,
  }),

  setSourcesError: (error: string | null): HotelQuestionnaireAction => ({
    type: 'SET_SOURCES_ERROR',
    payload: error,
  }),

  setReadonlyQuestionnaireLoading: (
    loading: boolean
  ): HotelQuestionnaireAction => ({
    type: 'SET_READONLY_QUESTIONNAIRE_LOADING',
    payload: loading,
  }),

  setReadonlyQuestionnaireData: (
    data: GroupedQuestionnaireData | StandardQuestionnaireData
  ): HotelQuestionnaireAction => ({
    type: 'SET_READONLY_QUESTIONNAIRE_DATA',
    payload: data,
  }),

  setReadonlyQuestionnaireError: (
    error: string | null
  ): HotelQuestionnaireAction => ({
    type: 'SET_READONLY_QUESTIONNAIRE_ERROR',
    payload: error,
  }),

  resetState: (): HotelQuestionnaireAction => ({
    type: 'RESET_STATE',
  }),

  updateQuestionAttachments: (
    questionId: string,
    attachments: any[]
  ): HotelQuestionnaireAction => ({
    type: 'UPDATE_QUESTION_ATTACHMENTS',
    payload: { questionId, attachments },
  }),

  updateQuestionUploadedFilenames: (
    questionId: string,
    uploadedFilenames: string[]
  ): HotelQuestionnaireAction => ({
    type: 'UPDATE_QUESTION_UPLOADED_FILENAMES',
    payload: { questionId, uploadedFilenames },
  }),

  updateQuestionExclusionStates: (
    changedQuestionId: string
  ): HotelQuestionnaireAction => ({
    type: 'UPDATE_QUESTION_EXCLUSION_STATES',
    payload: changedQuestionId,
  }),

  cleanupExcludedQuestionAnswers: (): HotelQuestionnaireAction => ({
    type: 'CLEANUP_EXCLUDED_QUESTION_ANSWERS',
  }),

  setReviews: (reviews: Record<string, any>): HotelQuestionnaireAction => ({
    type: 'SET_REVIEWS',
    payload: reviews,
  }),

  setDraftReviews: (
    draftReviews: Record<string, any>
  ): HotelQuestionnaireAction => ({
    type: 'SET_DRAFT_REVIEWS',
    payload: draftReviews,
  }),

  clearDraftReviews: (): HotelQuestionnaireAction => ({
    type: 'CLEAR_DRAFT_REVIEWS',
  }),

  applyDraftReviews: (): HotelQuestionnaireAction => ({
    type: 'APPLY_DRAFT_REVIEWS',
  }),

  updateReview: (
    questionId: string,
    approved: boolean | null,
    reasonId?: string | null
  ): HotelQuestionnaireAction => ({
    type: 'UPDATE_REVIEW',
    payload: { questionId, approved, reasonId },
  }),

  updateDraftReview: (
    questionId: string,
    approved: boolean | null,
    reasonId?: string | null
  ): HotelQuestionnaireAction => ({
    type: 'UPDATE_DRAFT_REVIEW',
    payload: { questionId, approved, reasonId },
  }),

  setSubmissionsLoading: (loading: boolean): HotelQuestionnaireAction => ({
    type: 'SET_SUBMISSIONS_LOADING',
    payload: loading,
  }),

  setSubmissions: (submissions: any[]): HotelQuestionnaireAction => ({
    type: 'SET_SUBMISSIONS',
    payload: submissions,
  }),

  setSubmissionsError: (error: string | null): HotelQuestionnaireAction => ({
    type: 'SET_SUBMISSIONS_ERROR',
    payload: error,
  }),

  setSelectedSubmissionId: (submissionId: string | null): HotelQuestionnaireAction => ({
    type: 'SET_SELECTED_SUBMISSION_ID',
    payload: submissionId,
  }),

  setQuestionnaireType: (type: 'FASTTRACK' | 'FULL' | null): HotelQuestionnaireAction => ({
    type: 'SET_QUESTIONNAIRE_TYPE',
    payload: type,
  }),

  setSwitchingType: (value: boolean): HotelQuestionnaireAction => ({
    type: 'SET_SWITCHING_TYPE',
    payload: value,
  }),
};
